<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import type { BetaSocialLink, BetaSocialLinkInput } from '@/types/beta-dashboard'
import { betaSocialNetworkOptions } from '@/utils/beta-integrations'

const props = defineProps<{
    open: boolean
    integration?: BetaSocialLink | null
    saving?: boolean
}>()

const emit = defineEmits<{
    close: []
    save: [integration: BetaSocialLinkInput]
}>()

const api = ref(0)
const link = ref('')
const error = ref('')

watch(
    [() => props.open, () => props.integration],
    ([open, integration]) => {
        if (!open) return
        api.value = Number(integration?.api || 0)
        link.value = String(integration?.link || '')
        error.value = ''
    },
    { immediate: true },
)

const editing = computed(() => Boolean(props.integration?.id))

const close = () => {
    if (!props.saving) emit('close')
}

const save = () => {
    if (!api.value) {
        error.value = 'Selecione uma rede social.'
        return
    }
    if (!link.value.trim()) {
        error.value = 'Informe o endereço da rede social.'
        return
    }

    emit('save', {
        id: props.integration?.id ?? null,
        api: api.value,
        link: link.value.trim(),
    })
}
</script>

<template>
    <Teleport to="body">
        <div v-if="open" class="beta-social-link-dialog" role="dialog" aria-modal="true">
            <button type="button" class="beta-social-link-dialog__backdrop" aria-label="Fechar" @click="close"></button>

            <form class="beta-social-link-dialog__panel" @submit.prevent="save">
                <header>
                    <div>
                        <p>Integrações</p>
                        <h2>{{ editing ? 'Editar rede social' : 'Adicionar rede social' }}</h2>
                    </div>
                    <button type="button" class="is-close" aria-label="Fechar" @click="close">×</button>
                </header>

                <div class="beta-social-link-dialog__body">
                    <label>
                        <span>Rede social</span>
                        <select v-model.number="api">
                            <option :value="0" disabled>Selecione uma rede social</option>
                            <option v-for="option in betaSocialNetworkOptions" :key="option.id" :value="option.id">
                                {{ option.name }}
                            </option>
                        </select>
                    </label>

                    <label>
                        <span>Seu endereço</span>
                        <input v-model="link" type="text" placeholder="https://" maxlength="500">
                    </label>

                    <p v-if="error" class="beta-social-link-dialog__error">{{ error }}</p>
                </div>

                <footer>
                    <button type="button" class="is-cancel" :disabled="saving" @click="close">Cancelar</button>
                    <button type="submit" class="is-save" :disabled="saving">
                        {{ saving ? 'Salvando…' : editing ? 'Salvar alterações' : 'Adicionar' }}
                    </button>
                </footer>
            </form>
        </div>
    </Teleport>
</template>

<style scoped>
.beta-social-link-dialog {
    position: fixed;
    inset: 0;
    z-index: 110;
    display: grid;
    place-items: center;
    padding: 18px;
}

.beta-social-link-dialog__backdrop {
    position: absolute;
    inset: 0;
    background: rgba(20, 35, 57, 0.64);
    backdrop-filter: blur(7px);
}

.beta-social-link-dialog__panel {
    position: relative;
    width: min(500px, 100%);
    overflow: hidden;
    border-radius: 22px;
    background: #fff;
}

.beta-social-link-dialog__panel > header,
.beta-social-link-dialog__panel > footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 14px 18px;
}

.beta-social-link-dialog__panel > header {
    border-bottom: 1px solid #e8edf3;
}

.beta-social-link-dialog__panel > header p {
    color: var(--main-color);
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.16em;
    text-transform: uppercase;
}

.beta-social-link-dialog__panel > header h2 {
    margin-top: 2px;
    color: #172d4a;
    font-size: 21px;
    font-weight: 700;
}

.beta-social-link-dialog__panel .is-close {
    width: 34px;
    height: 34px;
    border-radius: 10px;
    background: #f0f4f9;
    color: #60718a;
    font-size: 18px;
}

.beta-social-link-dialog__body {
    display: grid;
    gap: 12px;
    padding: 15px 18px;
}

.beta-social-link-dialog__body label {
    display: grid;
    gap: 6px;
}

.beta-social-link-dialog__body label > span {
    color: #3a506d;
    font-size: 12px;
    font-weight: 700;
}

.beta-social-link-dialog__body input,
.beta-social-link-dialog__body select {
    width: 100%;
    border: 1px solid #dce4ef;
    border-radius: 12px;
    background: #fff;
    padding: 10px 11px;
    color: #172d4a;
    font-size: 11px;
    outline: none;
}

.beta-social-link-dialog__body input:focus,
.beta-social-link-dialog__body select:focus {
    border-color: var(--main-color);
    outline: 3px solid color-mix(in srgb, var(--main-color) 9%, transparent);
    outline-offset: 1px;
}

.beta-social-link-dialog__error {
    color: #b24c4c;
    font-size: 10px;
    font-weight: 700;
}

.beta-social-link-dialog__panel > footer {
    justify-content: flex-end;
    border-top: 1px solid #e8edf3;
}

.beta-social-link-dialog__panel > footer button {
    min-height: 36px;
    border-radius: var(--beta-button-radius);
    padding: 0 13px;
    font-size: 10px;
    font-weight: 700;
}

.beta-social-link-dialog__panel .is-cancel {
    color: #74859b;
}

.beta-social-link-dialog__panel .is-save {
    background: var(--main-color);
    color: #fff;
}

.beta-social-link-dialog__panel > footer button:disabled {
    cursor: wait;
    opacity: 0.55;
}
</style>
